import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "../_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const unitLabel = (u) => (u === "m3" ? "m³" : u === "load" ? "loads" : "t");
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export function SupplyYardReports() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { api.get(`/sy/reports?days=${days}`).then((r) => setD(r.data)).catch(() => {}); }, [days]);
  const s = d?.summary || {};
  const Section = ({ title, rows }) => (
    <div className="hard-card p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">{title}</div>
      {(!rows || rows.length === 0) ? <p className="text-sm text-zinc-500">No data.</p> : (
        <div className="space-y-1.5">{rows.slice(0, 8).map((r) => (
          <div key={r.name} className="flex items-center justify-between text-sm py-1 border-b border-zinc-100 dark:border-white/5">
            <span className="truncate">{r.name}</span>
            <span className="text-right shrink-0"><span className="font-mono">{r.t ? `${r.t.toFixed(1)}t ` : ""}{r.m3 ? `${r.m3.toFixed(1)}m³ ` : ""}{r.load ? `${r.load} loads` : ""}</span> <span className="font-bold ml-2">{money(r.value)}</span></span>
          </div>
        ))}</div>
      )}
    </div>
  );
  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 md:max-w-[1600px] md:mx-auto pb-24 md:pb-10" data-testid="sy-reports-page">
      <PageHeader tag="supply yard / reports" title="Yard Reports" subtitle="Visits, weighbridge throughput, materials, product sales and processing."
        action={<div className="flex gap-1.5">{[7, 30, 90].map((n) => <button key={n} onClick={() => setDays(n)} className={`px-3 py-1 rounded-full text-[11px] uppercase font-bold border ${days === n ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300"}`} data-testid={`report-days-${n}`}>{n}d</button>)}</div>} />
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        {[["Visits", s.visits], ["Loads in", s.inbound_loads], ["Loads out", s.outbound_loads],
          ["Tipping rev", money(s.tipping_revenue)], ["Product sales", money(s.product_sales)],
          ["Avg visit", s.avg_visit_minutes != null ? `${s.avg_visit_minutes}m` : "—"]].map(([l, v]) => (
          <div key={l} className="hard-card p-3 text-center"><div className="text-xl font-display font-extrabold">{v ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mt-0.5">{l}</div></div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="hard-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">Visit types</div>
          {Object.entries(d?.visit_types || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm py-1 border-b border-zinc-100 dark:border-white/5"><span>{k.replace(/_/g, " ")}</span><span className="font-mono font-bold">{v}</span></div>
          ))}
        </div>
        <Section title="Material received" rows={d?.material_received} />
        <Section title="Products sold" rows={d?.products_sold} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Sales by customer" rows={d?.by_customer_out} />
        <div className="hard-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">Processing batches</div>
          {(d?.processing || []).length === 0 ? <p className="text-sm text-zinc-500">No batches.</p> : (
            <div className="space-y-1.5">{d.processing.slice(0, 8).map((b) => (
              <div key={b.batch_no} className="flex items-center justify-between text-sm py-1 border-b border-zinc-100 dark:border-white/5">
                <span className="font-bold">{b.batch_no} <span className="text-zinc-400 font-normal">{b.date}</span></span>
                <span className="font-mono">{b.yield_pct != null ? `${b.yield_pct}% yield` : "—"}</span>
              </div>
            ))}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SupplyYardSettings() {
  const [s, setS] = useState(null);
  const load = useCallback(() => { api.get("/sy/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.put("/sy/settings", { ...s, truck_types: (s.truck_types || []).filter((t) => t && t.name && t.name.trim()) }); toast.success("Settings saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!s) return <div className="p-8" data-testid="sy-settings-page">Loading…</div>;
  const types = s.truck_types || [];
  const setType = (i, k, v) => setS({ ...s, truck_types: types.map((t, idx) => idx === i ? { ...t, [k]: v } : t) });
  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 md:max-w-[1000px] md:mx-auto pb-24 md:pb-10" data-testid="sy-settings-page">
      <PageHeader tag="supply yard / settings" title="Supply Yard Settings" subtitle="Truck types, weighbridge and stock policy."
        action={<Button onClick={save} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="settings-save">Save</Button>} />

      <div className="hard-card p-5 mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">Weighbridge</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.weighbridge_enabled} onChange={(e) => setS({ ...s, weighbridge_enabled: e.target.checked })} data-testid="wb-enabled" /> Weighbridge enabled</label>
          <div><Label className="text-[10px] uppercase font-bold">Weighbridge name</Label><Input value={s.weighbridge_name || ""} onChange={(e) => setS({ ...s, weighbridge_name: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.allow_known_tare} onChange={(e) => setS({ ...s, allow_known_tare: e.target.checked })} data-testid="allow-known-tare" /> Allow known tare for inbound</label>
          <div><Label className="text-[10px] uppercase font-bold">Known tare max age (days)</Label><Input type="number" value={s.known_tare_max_age_days ?? ""} onChange={(e) => setS({ ...s, known_tare_max_age_days: Number(e.target.value) })} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.require_outbound_weight} onChange={(e) => setS({ ...s, require_outbound_weight: e.target.checked })} /> Require outbound weighbridge weight</label>
          <div><Label className="text-[10px] uppercase font-bold">Processing variance tolerance (%)</Label><Input type="number" value={s.variance_tolerance_pct ?? ""} onChange={(e) => setS({ ...s, variance_tolerance_pct: Number(e.target.value) })} /></div>
        </div>
        <p className="text-[11px] text-zinc-400 mt-2">Weighbridge capture is manual entry — no hardware integration is connected.</p>
      </div>

      <div className="hard-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold">Truck types</div>
          <button onClick={() => setS({ ...s, truck_types: [...types, { name: "", estimated_capacity: null, unit: "m3" }] })} className="text-xs text-[#FF5F15] font-bold flex items-center gap-1" data-testid="add-truck-type"><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
        <div className="space-y-2">{types.map((t, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={t.name} onChange={(e) => setType(i, "name", e.target.value)} placeholder="Truck type" className="flex-1" data-testid={`truck-type-name-${i}`} />
            <Input type="number" value={t.estimated_capacity ?? ""} onChange={(e) => setType(i, "estimated_capacity", e.target.value === "" ? null : Number(e.target.value))} placeholder="Est. cap" className="w-28" />
            <span className="text-xs text-zinc-400 w-8">{unitLabel(t.unit)}</span>
            <button onClick={() => setS({ ...s, truck_types: types.filter((_, idx) => idx !== i) })} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}</div>
        <p className="text-[11px] text-zinc-400 mt-2">Estimated capacity is a guide only — never treated as a measured weight or volume.</p>
      </div>
    </div>
  );
}
